import { domToBlob } from 'modern-screenshot';
import { DrawEMACloudBuySellArrowComponent } from '../../components/draw-ema-cloud-buy-sell-arrow.component';
import { PnlComponent } from '../../components/pnl.component';
import { TradeComponent } from "../../components/trade.component";
import { TraceLoggingHelpers } from "../../helpers/trace-logging-helpers";
import { WebChartThemeHelpers } from "../../helpers/webchart-theme-helpers";
import { ChartTypeToEnumMapper } from "../../js-api-mappers/chart-type/chart-type-to-enum.mapper";
import { ChartTimeFrameToTimeFrameMapper } from "../../js-api-mappers/timeframe/chart-time-frame-to-time-frame.mapper";
import { TimeFrameToChartTimeFrameMapper } from "../../js-api-mappers/timeframe/time-frame-to-chart-time-frame.mapper";
import { WebChartDataService } from "../../services/web-chart-data.service";
import { WebChartsMarketingContainer } from "../../web-charts-marketing.container";
import { IChartContainer } from "../base/chart-container.interface";
import { Timeframe } from "../enum/timeframe.enum";
import { SingleStockWindowChangeFromCloseManager } from '../single-stock-window/single-stock-window-change-from-close.manager';

declare const $: any;
declare const window: any;

export enum ChartMode {
    Standard = 'Standard',
    Preview = 'Preview',
    PopupChart = 'PopupChart',
}

export enum ChartTheme {
    Dark = 'Dark',
    Black = 'Black',
    LightBlue = 'LightBlue',
    Light = 'Light',
    UpdatedWebDark = 'UpdatedWebDark'
}

export class MarketingWebChartWindowWrapper {

    private chartReady = false;

    private chartReadyWatchers: Array<Function> = [];

    private chartContainer: IChartContainer;
    private symbolLookupElement: HTMLElement | null = null;
    constructor(base: IChartContainer) {
        this.chartContainer = base;
        this.symbolLookupElement = this.chartContainer.element.findFirst('input.chart-symbol');
        this.onSetSymbolAndTimeframeListener();
    }

    public onChartReady() {
        this.chartReady = true;

        if (this.chartReadyWatchers.length) {
            for (const fx of this.chartReadyWatchers) {
                fx();
            }
        }

        this.chartReadyWatchers = [];
    }

    private onSetSymbolAndTimeframeListener() {
        const setSymbol = (symbol: string) => {
            if (!this.chartReady) {
                this.chartReadyWatchers.push(() => {
                    this.setSymbol(symbol);
                });
            } else {
                this.setSymbol(symbol);
            }
        };

        const setMainTimeframe = (timeframeString: string) => {
            if (!this.chartReady) {
                this.chartReadyWatchers.push(() => {
                    this.setMainTimeframe(timeframeString);
                });
            } else {
                this.setMainTimeframe(timeframeString);
            }
        };

        const setPipTimeframe = (timeframeString: string) => {
            if (!this.chartReady) {
                this.chartReadyWatchers.push(() => {
                    this.setPipTimeframe(timeframeString);
                });
            } else {
                this.setPipTimeframe(timeframeString);
            }
        };

        const setPipTimespan = (timespanString: string) => {
            if (!this.chartReady) {
                this.chartReadyWatchers.push(() => {
                    this.setPipTimespan(timespanString);
                });
            } else {
                this.setPipTimespan(timespanString);
            }
        };

        const setTimeframeStyle = (timeframeString: string) => {
            if (!this.chartReady) {
                this.chartReadyWatchers.push(() => {
                    this.setTimeframeStyle(timeframeString);
                });
            } else {
                this.setTimeframeStyle(timeframeString);
            }
        };

        const setTheme = (themeString: string) => {
            if (!this.chartReady) {
                this.chartReadyWatchers.push(() => {
                    this.setTheme(themeString);
                });
            } else {
                this.setTheme(themeString);
            }
        };

        const setSelectedAccount = (account: string) => {
            if (!this.chartReady) {
                this.chartReadyWatchers.push(() => {
                    this.setSelectedAccount(account);
                });
            } else {
                this.setSelectedAccount(account);
            }
        };

        const setTradeAccount = (account: string) => {
            if (!this.chartReady) {
                this.chartReadyWatchers.push(() => {
                    this.setTradeAccount(account);
                });
            } else {
                this.setTradeAccount(account);
            }
        };

        const setTradeShares = (shares: number) => {
            if (!this.chartReady) {
                this.chartReadyWatchers.push(() => {
                    this.setTradeShares(shares);
                });
            } else {
                this.setTradeShares(shares);
            }
        };

        const setMainChartLineAndFillColor = (lineHexColor: string, areaFillHexColor: string, gradientHexColor: string) => {
            if (!this.chartReady) {
                this.chartReadyWatchers.push(() => {
                    this.setMainChartLineAndFillColor(lineHexColor, areaFillHexColor, gradientHexColor);
                });
            } else {
                this.setMainChartLineAndFillColor(lineHexColor, areaFillHexColor, gradientHexColor);
            }
        };

        const setUpColor = (colorString: string) => {
            if (!this.chartReady) {
                this.chartReadyWatchers.push(() => {
                    this.setUpColor(colorString);
                });
            } else {
                this.setUpColor(colorString);
            }
        };

        const setDownColor = (colorString: string) => {
            if (!this.chartReady) {
                this.chartReadyWatchers.push(() => {
                    this.setDownColor(colorString);
                });
            } else {
                this.setDownColor(colorString);
            }
        };

        const setPreviewSizePercent = (percentage: number) => {
            if (!this.chartReady) {
                this.chartReadyWatchers.push(() => {
                    this.setPreviewSizePercent(percentage);
                });
            } else {
                this.setPreviewSizePercent(percentage);
            }
        };

        const setUseLoginPopup = (on: boolean) => {
            if (!this.chartReady) {
                this.chartReadyWatchers.push(() => {
                    this.setUseLoginPopup(on);
                });
            } else {
                this.setUseLoginPopup(on);
            }
        };


        const setChartType = (chartType: string) => {
            if (!this.chartReady) {
                this.chartReadyWatchers.push(() => {
                    this.setChartType(chartType);
                });
            } else {
                this.setChartType(chartType);
            }
        };

        const setDebugTrace = (on: boolean) => {
            if (!this.chartReady) {
                this.chartReadyWatchers.push(() => {
                    this.setDebugTrace(on);
                });
            } else {
                this.setDebugTrace(on);
            }
        };

        const getSelectedCompanyName = (): string => {
            return this.getSelectedCompanyName();
        };

        const afterResize = () => {
            this.afterResize();
        };

        const getSelectedSymbol = (): string => {
            return this.getSelectedSymbol();
        };

        const getMainChartSelectedTimeframe = (): string => {
            return this.getMainChartSelectedTimeframe();
        };

        const getPipChartSelectedTimeframe = (): string => {
            return this.getPipChartSelectedTimeframe();
        };

        const emaCloud_Long_validateEmaFlag = (date: Date): any => {
            return this.emaCloud_Long_validateEmaFlag(date);
        };

        const emaCloud_Short_validateEmaFlag = (date: Date): any => {
            return this.emaCloud_Short_validateEmaFlag(date);
        };

        const twitterImageExport = (callback: (canvasBlob: any) => void, renderTestImage?: boolean) => {
            this.twitterImageExport(callback, renderTestImage);
        };

        const getChangeFromCloseValues = () => {
            return this.getChangeFromCloseValues();
        };

        const getShouldUseTopListForChangeFromClose = (currentTime: Date) => {
            return this.getShouldUseTopListForChangeFromClose(currentTime);
        };

        const setChangeFromClose_FromTopList = (changeFromCloseDollar: number, changeFromClosePercentage: number) => {
            this.setChangeFromClose_FromTopList(changeFromCloseDollar, changeFromClosePercentage);
        };

        const setChangeFromClose_FromLastMarketOpen = (changeFromCloseDollar: number, changeFromClosePercentage: number) => {
            this.setChangeFromClose_FromLastMarketOpen(changeFromCloseDollar, changeFromClosePercentage);
        };

        const getLastMarketClose_ChangeFromClose = (date: Date, lastPrice?: number) => {
            return this.getLastMarketClose_ChangeFromClose(date, lastPrice);
        };

        const getLastPriceInLastMarketOpenDay = async (date: Date) => {
            return this.getLastPriceInLastMarketOpenDay(date);
        };

        const addTemporaryPosition = (accountName: string, shareCount: number, averageCost: number) => {
            return this.addTemporaryPosition(accountName, shareCount, averageCost);
        };

        let symbolLookupElement = this.chartContainer.element.findFirst('input.chart-symbol');

        if (!window.chart) {
            window.chart = {};
            window.chart.setSymbol = setSymbol;
            window.chart.setTimeframe = setMainTimeframe;
            window.chart.setMainTimeframe = setMainTimeframe;
            window.chart.setPipTimeframe = setPipTimeframe;
            window.chart.setPipTimespan = setPipTimespan;
            window.chart.setTimeframeStyle = setTimeframeStyle;
            window.chart.setTheme = setTheme;
            window.chart.setMainChartLineAndFillColor = setMainChartLineAndFillColor;
            window.chart.setUpColor = setUpColor;
            window.chart.setDownColor = setDownColor;
            window.chart.setPreviewSizePercent = setPreviewSizePercent;
            window.chart.setUseLoginPopup = setUseLoginPopup;
            window.chart.setSelectedAccount = setSelectedAccount;
            window.chart.setTradeAccount = setTradeAccount;
            window.chart.setTradeShares = setTradeShares;
            window.chart.setChartType = setChartType;
            window.chart.setDebugTrace = setDebugTrace;
            window.chart.getSelectedCompanyName = getSelectedCompanyName;
            window.chart.afterResize = afterResize;
            window.chart.getSelectedSymbol = getSelectedSymbol;
            window.chart.getMainChartSelectedTimeframe = getMainChartSelectedTimeframe;
            window.chart.getPipChartSelectedTimeframe = getPipChartSelectedTimeframe;
            window.chart.twitterImageExport = twitterImageExport;
            window.chart.getChangeFromCloseValues = getChangeFromCloseValues;
            window.chart.getShouldUseTopListForChangeFromClose = getShouldUseTopListForChangeFromClose;
            window.chart.setChangeFromClose_FromTopList = setChangeFromClose_FromTopList;
            window.chart.setChangeFromClose_FromLastMarketOpen = setChangeFromClose_FromLastMarketOpen;
            window.chart.getLastMarketClose_ChangeFromClose = getLastMarketClose_ChangeFromClose;
            window.chart.getLastPriceInLastMarketOpenDay = getLastPriceInLastMarketOpenDay;
            window.chart.addTemporaryPosition = addTemporaryPosition;
            window.chart.symbolLookup = symbolLookupElement;
            window.chart.emaCloud_Long_validateEmaFlag = emaCloud_Long_validateEmaFlag;
            window.chart.emaCloud_Short_validateEmaFlag = emaCloud_Short_validateEmaFlag;
        }

        let existingChartMap = new Map();
        if (window.chartsMap) {
            existingChartMap = window.chartsMap;
        }

        if (!existingChartMap.has(this.chartContainer.id)) {
            existingChartMap.set(this.chartContainer.id, {
                id: this.chartContainer.id,
                setSymbol: setSymbol,
                setMainTimeframe: setMainTimeframe,
                setPipTimeframe: setPipTimeframe,
                setPipTimespan: setPipTimespan,
                setTimeframeStyle: setTimeframeStyle,
                setTheme: setTheme,
                setMainChartLineAndFillColor: setMainChartLineAndFillColor,
                setUpColor: setUpColor,
                setDownColor: setDownColor,
                setPreviewSizePercent: setPreviewSizePercent,
                setUseLoginPopup: setUseLoginPopup,
                setSelectedAccount: setSelectedAccount,
                setTradeAccount: setTradeAccount,
                setTradeShares: setTradeShares,
                setChartType: setChartType,
                setDebugTrace: setDebugTrace,
                getSelectedCompanyName: getSelectedCompanyName,
                afterResize: afterResize,
                getSelectedSymbol: getSelectedSymbol,
                getMainChartSelectedTimeframe: getMainChartSelectedTimeframe,
                getPipChartSelectedTimeframe: getPipChartSelectedTimeframe,
                twitterImageExport: twitterImageExport,
                emaCloud_Long_validateEmaFlag: emaCloud_Long_validateEmaFlag,
                emaCloud_Short_validateEmaFlag: emaCloud_Short_validateEmaFlag,
                getChangeFromCloseValues: getChangeFromCloseValues,
                getShouldUseTopListForChangeFromClose: getShouldUseTopListForChangeFromClose,
                setChangeFromClose_FromTopList: setChangeFromClose_FromTopList,
                setChangeFromClose_FromLastMarketOpen: setChangeFromClose_FromLastMarketOpen,
                getLastMarketClose_ChangeFromClose: getLastMarketClose_ChangeFromClose,
                getLastPriceInLastMarketOpenDay: getLastPriceInLastMarketOpenDay,
                addTemporaryPosition: addTemporaryPosition,
                symbolLookup: symbolLookupElement
            });
        }

        if (existingChartMap.size > 0) {
            const sortedChartArray = Array.from(existingChartMap);
            const chartsById: Array<any> = [];
            const charts: Array<any> = [];

            for (let i = 0; i < sortedChartArray.length; i++) {
                const id = sortedChartArray[i][0];
                const chart = sortedChartArray[i][1];
                chartsById[id] = chart;
                charts[i] = chart;

                if (this.chartContainer.id == id) {
                    this.chartContainer.index = i;
                }
            }

            window.chartsById = chartsById;
            window.charts = charts;
        }

        window.chartsMap = existingChartMap;
    }

    private setSymbol(symbol: string) {
        if (!symbol) {
            return;
        }

        const chartContainer = this.chartContainer as WebChartsMarketingContainer;
        const symbolUpperCase = symbol.toUpperCase();
        if (symbolUpperCase == this.getSelectedSymbol().toUpperCase()) {
            return;
        }

        if (this.symbolLookupElement && document.activeElement == this.symbolLookupElement) {
            this.symbolLookupElement.blur();
        }

        chartContainer.chartService.requestNewSymbol(symbolUpperCase);
        chartContainer.pnpContainer?.chartService.requestNewSymbol(symbolUpperCase);
    }

    private setMainTimeframe(timeframeString: string) {
        if (!timeframeString) {
            return;
        }

        const chartContainer = this.chartContainer as WebChartsMarketingContainer;
        const timeframeStringUpperCase = timeframeString.toUpperCase();
        const timeframe = TimeFrameToChartTimeFrameMapper.map(timeframeStringUpperCase);
        chartContainer.chartService.requestNewTimeframe(timeframe);
    }

    private setPipTimeframe(timeframeString: string) {
        if (!timeframeString) {
            return;
        }

        const chartContainer = this.chartContainer as WebChartsMarketingContainer;
        const timeframeStringUpperCase = timeframeString.toUpperCase();
        const timeframe = TimeFrameToChartTimeFrameMapper.map(timeframeStringUpperCase);
        chartContainer.pnpContainer?.chartService.requestNewTimeframe(timeframe);
    }

    private setPipTimespan(timespanString: string) {
        if (!timespanString) {
            return;
        }

        const chartContainer = this.chartContainer as WebChartsMarketingContainer;
        const timespanStringUpperCase = timespanString.toUpperCase();
        const timeframe = TimeFrameToChartTimeFrameMapper.mapTimespan(timespanStringUpperCase);
        chartContainer.pnpContainer?.chartService.requestNewTimeframe(timeframe);
    }

    private setTimeframeStyle(timeframeString: string) {
        const timeFrameStyle = timeframeString?.trim()?.toLowerCase() ?? '';
        if (!timeFrameStyle) {
            return;
        }

        let mainTimeframe = Timeframe.Day;
        let pipTimeframe = Timeframe.OneMonthRange;
        if (timeFrameStyle == 'daytrading') {
            mainTimeframe = Timeframe.FifteenMinute;
            pipTimeframe = Timeframe.Day;
        } else if (timeFrameStyle == 'swingtrading') {
            mainTimeframe = Timeframe.Day;
            pipTimeframe = Timeframe.FiveYearRange;
        } else if (timeFrameStyle == 'investing') {
            mainTimeframe = Timeframe.Week;
            pipTimeframe = Timeframe.FiveYearRange;
        } else {
            return;
        }
        
        const chartContainer = this.chartContainer as WebChartsMarketingContainer;
        chartContainer.chartService.requestNewTimeframe(mainTimeframe);
        chartContainer.pnpContainer?.chartService.requestNewTimeframe(pipTimeframe);
    }

    private setTheme(themeString: string) {
        if (!themeString) {
            return;
        }

        const theme = WebChartThemeHelpers.getTheme(themeString.toLowerCase());

        const chartContainer = this.chartContainer as WebChartsMarketingContainer;
        const chartService = chartContainer.chartService as WebChartDataService;
        const pnpChartService = chartContainer.pnpContainer?.chartService as WebChartDataService;

        chartService.setChartStyleData(theme);
        chartService.onSubscribeChartStyleData();

        pnpChartService?.setChartStyleData(theme);
        pnpChartService?.onSubscribeChartStyleData();
    }

    private setSelectedAccount(account: string) {
        const pnlComponent = this.chartContainer.getComponentsByType(PnlComponent)?.[0] as PnlComponent;
        if (!account || !pnlComponent) return;

        pnlComponent.setSelectPositionByAccount(account);
    }

    private setTradeAccount(account: string) {
        const tradeComponent = this.chartContainer.getComponentsByType(TradeComponent)?.[0] as TradeComponent;
        if (!account || !tradeComponent) return;

        tradeComponent.setAccount(account);
    }

    private setTradeShares(shares: number) {
        const tradeComponent = this.chartContainer.getComponentsByType(TradeComponent)?.[0] as TradeComponent;
        if (!tradeComponent) return;

        const sharesParsed = parseInt(shares + '', 10);
        if (isNaN(sharesParsed)) return;

        tradeComponent.setShares(sharesParsed);
    }

    private setUpColor(colorString: string) {
        const chartContainer = this.chartContainer as WebChartsMarketingContainer;
        const chartService = chartContainer.chartService as WebChartDataService;
        chartService.setSelectedColor(colorString, true);
        chartService.onSubscribeInitChartData();
    }

    private setDownColor(colorString: string) {
        const chartContainer = this.chartContainer as WebChartsMarketingContainer;
        const chartService = chartContainer.chartService as WebChartDataService;
        chartService.setSelectedColor(colorString, false);
        chartService.onSubscribeInitChartData();
    }

    private setMainChartLineAndFillColor(lineHexColor: string, areaFillHexColor: string, gradientHexColor: string) {
        const chartContainer = this.chartContainer as WebChartsMarketingContainer;
        const chartService = chartContainer.chartService as WebChartDataService;
        chartService.setMainChartLineAndFillColor(lineHexColor, areaFillHexColor, gradientHexColor);
        chartService.onSubscribeInitChartData();
    }

    private setPreviewSizePercent(percentage: number) {
        const chartContainer = this.chartContainer as WebChartsMarketingContainer;
        const chartService = chartContainer.chartService as WebChartDataService;
        chartService.setChartPreviewSymbolSizePercent(percentage);
        chartService.onSubscribeInitChartData();
    }

    private setUseLoginPopup(on: boolean) {
        const chartContainer = this.chartContainer as WebChartsMarketingContainer;
        const chartService = chartContainer.chartService as WebChartDataService;
        chartService.setUseLoginPopup(on);
        chartService.logout();

        chartContainer.handleLogin();
    }

    private setChartType(chartTypeString: string) {
        const chartContainer = this.chartContainer as WebChartsMarketingContainer;
        const chartService = chartContainer.chartService as WebChartDataService;
        const chartType = ChartTypeToEnumMapper.map(chartTypeString);
        chartService.setChartType(chartType);
        chartService.onSubscribeChartStyleData();
        chartService.onSubscribeInitChartData();
    }

    private setDebugTrace(on: boolean) {
        const chartContainer = this.chartContainer as WebChartsMarketingContainer;
        const chartService = chartContainer.chartService as WebChartDataService;
        chartService.setDebugTrace(on);
        chartService.onSubscribeInitChartData();
    }

    private getSelectedCompanyName(): string {
        const chartContainer = this.chartContainer as WebChartsMarketingContainer;
        const chartService = chartContainer.chartService as WebChartDataService;
        return chartService.getCompanyName();
    }

    private afterResizeTimer: any;
    private afterResize() {
        const chartContainer = this.chartContainer as WebChartsMarketingContainer;
        chartContainer?.tiChartSeries?.clearEMAClouldData();

        clearTimeout(this.afterResizeTimer);
        this.afterResizeTimer = setTimeout(() => {
            chartContainer?.tiChartSeries?.afterResize();
            chartContainer?.singleStockWindow?.setContentSize();
            
            chartContainer?.pnpContainer?.tiChartSeries?.afterResize();
            chartContainer?.pnpContainer?.setPnPSize();

            setTimeout(() => {
                chartContainer?.tiChartSeries?.setEMACloundData();
            }, 200);
        }, 200);
    }

    private getSelectedSymbol(): string {
        return this.chartContainer?.tiChartSeries?.symbol ?? '';
    }

    private getMainChartSelectedTimeframe(): string {
        const chartContainer = this.chartContainer as WebChartsMarketingContainer;
        const timeframe = chartContainer?.tiChartSeries?.currentTimeframe;
        if (!timeframe) {
            return '';
        }

        return ChartTimeFrameToTimeFrameMapper.map(timeframe);
    }

    private getPipChartSelectedTimeframe(): string {
        const chartContainer = this.chartContainer as WebChartsMarketingContainer;
        const timeframe = chartContainer?.pnpContainer?.tiChartSeries?.currentTimeframe;
        if (!timeframe) {
            return '';
        }

        return ChartTimeFrameToTimeFrameMapper.map(timeframe);
    }

    private emaCloud_Long_validateEmaFlag(date: Date): any {
        return (this.chartContainer.getComponentsByType(DrawEMACloudBuySellArrowComponent)?.[0] as DrawEMACloudBuySellArrowComponent).emaCloud_Long_validateEmaFlag(date);
    }

    private emaCloud_Short_validateEmaFlag(date: Date): any {
        return (this.chartContainer.getComponentsByType(DrawEMACloudBuySellArrowComponent)?.[0] as DrawEMACloudBuySellArrowComponent).emaCloud_Short_validateEmaFlag(date);
    }

    private twitterImageExport(callback: (canvasBlob: any) => void, renderTestImage?: boolean) {
        const date = new Date();
        const chartContainer = this.chartContainer as WebChartsMarketingContainer;;
        let htmlContainer = chartContainer.chartWrapper.closest<HTMLElement>('.card');
        if (!htmlContainer) {
            htmlContainer = chartContainer.chartWrapper;
        }

        const isFullScreen = htmlContainer.clientWidth == document.body.clientWidth;
        const parentContainer = $(htmlContainer).parent();
        
        if (isFullScreen) {
            $(htmlContainer).css('z-index', `1020`);
        }

        const elementClone = $(htmlContainer).clone();

        const existingHtmlContainerStyles = $(htmlContainer).attr('style');
        const existingchartWrapperStyles = $(chartContainer.chartWrapper).attr('style');

        const chartfinishRender = () => {          
            if (existingHtmlContainerStyles) {
                $(htmlContainer).attr('style', existingHtmlContainerStyles);
            } else {
                $(htmlContainer).attr('style', '');
            }

            if (existingchartWrapperStyles) {
                $(chartContainer.chartWrapper).attr('style', existingchartWrapperStyles);
            } else {
                $(chartContainer.chartWrapper).attr('style', '');
            }

            $(htmlContainer).find('.ti-logo').attr('style', '');
            $(htmlContainer).find('.ti-logo img').attr('style', '');
            $(htmlContainer).find('.symbol-lookup-wrapper input').css('height', '');
            $(htmlContainer).find('.symbol-lookup-wrapper input').css('text-transform', '');
            $(htmlContainer).find('.trade-ui-group button').css('width', '');
            
            $(htmlContainer).prependTo(parentContainer);
           
            setTimeout(() => {
                $(elementClone).remove();
                $(parentContainer).attr('style', '');
                $(parentContainer).find('.card-actions').attr('style', 'position: relative; z-index: 3;');
                reflow();
            }, 500);
        };

        const reflow = () => {            
            chartContainer.tiChartSeries?.highStockChart?.reflow();
            chartContainer.tiChartSeries?.afterResize();

            chartContainer.pnpContainer?.tiChartSeries?.highStockChart?.reflow();
            chartContainer.pnpContainer?.tiChartSeries?.afterResize();
        };

        $(parentContainer).attr('style', `position: relative !important; height: ${$(htmlContainer).height()}px !important;`);
        $(parentContainer).find('.card-actions').attr('style', 'opacity: 0 !important; visibility: hidden !important;');
 
        $(document.body).css('overflow', `hidden`);
        if (!isFullScreen) {
            $(elementClone).attr('style', `position: absolute !important; z-index: 100; width: ${$(htmlContainer).width()}px !important;`);
        } 

        setTimeout(() => {
            $(elementClone).prependTo(parentContainer);

            $(htmlContainer).attr('style', 'width: 1200px !important;');
            $(chartContainer.chartWrapper).attr('style', 'height: 550px !important;');
            $(htmlContainer).find('.ti-logo').attr('style', 'width: 240px !important; height: 100px !important;');
            $(htmlContainer).find('.ti-logo img').attr('style', 'width: 280px !important; height: 120px !important; object-fit: cover;');
            $(htmlContainer).find('.symbol-lookup-wrapper input').css('height', '22px');
            $(htmlContainer).find('.symbol-lookup-wrapper input').css('text-transform', 'uppercase !important');
            $(htmlContainer).find('.trade-ui-group button').css('width', '80px');

            const symbolValue = $(htmlContainer).find('.symbol-lookup-wrapper input').val();
            if (symbolValue) {
                $(htmlContainer).find('.symbol-lookup-wrapper input').val(symbolValue.toUpperCase());
            }

            $(htmlContainer).appendTo(document.body);
            reflow();
        }, 200);

        setTimeout(() => {
            try {
                domToBlob(htmlContainer, {
                    debug: true,
                    width: htmlContainer.clientWidth,
                    height: htmlContainer.clientHeight,
                    quality: 0.8
                }).then(blob => {

                    chartfinishRender();
                    callback(blob);

                    if (renderTestImage == true) {
                        const imageUrl = URL.createObjectURL(blob);
                        const img = document.createElement('img');
                        img.src = imageUrl;
                        document.body.appendChild(img);
                        TraceLoggingHelpers.log('screenshot renderTestImage duration(s)', (new Date().valueOf() - date.valueOf()));
                    }
                }).catch(() => {
                    chartfinishRender();
                });
            } catch {
                chartfinishRender();
            }

            try {
                // this.chartToBlob(chartContainer.tiChartSeries?.highStockChart)

                // html2canvas(htmlContainer, { logging: true, allowTaint: false, useCORS: true, letterRendering: false } as any).then(canvas => {
                //     // // FOR TESTING ONLY.
                //     if (renderTestImage == true) {
                //         document.body.appendChild(canvas);
                //         TraceLoggingHelpers.log('screenshot 5', (new Date().valueOf() - date.valueOf()));
                //     }
                // chartfinishRender();
                //     // // Convert the canvas object to a blob.
                //     canvas.toBlob((blob: any) => {
                //         console.log('blob', blob)
                //         TraceLoggingHelpers.log('screenshot 4', (new Date().valueOf() - date.valueOf()))
                //         callback(blob);
                //     });
                // }).catch(() => {
                //     chartfinishRender();
                // });
            } catch {
                chartfinishRender();
            }
            
            // chartfinishRender();
        }, 500);
    }

    private getChangeFromCloseValues() {
        return (this.chartContainer.getComponentsByType(SingleStockWindowChangeFromCloseManager)?.[0] as SingleStockWindowChangeFromCloseManager)?.getChangeFromCloseValues();
    }

    private getShouldUseTopListForChangeFromClose(currentTime: Date) {
        return (this.chartContainer.getComponentsByType(SingleStockWindowChangeFromCloseManager)?.[0] as SingleStockWindowChangeFromCloseManager)?.getShouldUseTopListForChangeFromClose(currentTime);
    }

    private setChangeFromClose_FromTopList(changeFromCloseDollar: number, changeFromClosePercentage: number) {
        return (this.chartContainer.getComponentsByType(SingleStockWindowChangeFromCloseManager)?.[0] as SingleStockWindowChangeFromCloseManager)?.setChangeFromClose_FromTopList(changeFromCloseDollar, changeFromClosePercentage);
    }

    private setChangeFromClose_FromLastMarketOpen(changeFromCloseDollar: number, changeFromClosePercentage: number) {
        return (this.chartContainer.getComponentsByType(SingleStockWindowChangeFromCloseManager)?.[0] as SingleStockWindowChangeFromCloseManager)?.setChangeFromClose_FromLastMarketOpen(changeFromCloseDollar, changeFromClosePercentage);
    }

    private getLastMarketClose_ChangeFromClose(date: Date, lastPrice?: number) {
        return (this.chartContainer.getComponentsByType(SingleStockWindowChangeFromCloseManager)?.[0] as SingleStockWindowChangeFromCloseManager)?.getLastMarketClose_ChangeFromClose(date, lastPrice);
    }
    
    private async getLastPriceInLastMarketOpenDay(date: Date) {
        return (this.chartContainer.getComponentsByType(SingleStockWindowChangeFromCloseManager)?.[0] as SingleStockWindowChangeFromCloseManager)?.getLastPriceInLastMarketOpenDay(date);
    }
    
    private addTemporaryPosition(accountName: string, shareCount: number, averageCost: number) {
        const chartContainer = this.chartContainer as WebChartsMarketingContainer;
        const chartService = chartContainer.chartService as WebChartDataService;
        chartService.addTemporaryPosition(accountName, shareCount, averageCost);
    }
    
    // private chartToBlob(chart, callback?) {
    //     const svg = chart.getSVG();
    //     // const canvas = document.createElement('canvas');
    //     // const ctx = canvas.getContext('2d');
    //     // const DOMURL = window.URL || window.webkitURL || window;
    //     // const img = new Image();
    //     // chart.exportChart({
    //     //     type: 'image/png', // or 'image/jpeg', 'image/svg+xml', 'application/pdf'
    //     //     filename: 'my-chart',
    //     //     scale: 2, // Scaling factor for the image
    //     //     sourceWidth: 800, // Width of the exported image
    //     //     sourceHeight: 600, // Height of the exported image
    //     //     chartOptions: {
    //     //       // Additional chart options for export
    //     //     }
    //     //   });

    //     // const svgBlob = new Blob([svg], { type: 'image/svg+xml;charset=utf-8' });
    //     // const url = DOMURL.createObjectURL(svgBlob);
    
    //     // img.onload = function () {
    //     //   canvas.width = img.width;
    //     //   canvas.height = img.height;
    //     //   ctx.drawImage(img, 0, 0);
    //     //   DOMURL.revokeObjectURL(url);
    //     // document.body.appendChild(canvas);
    //     // //   canvas.toBlob(function (blob) {
    //     // //     callback(blob);
    //     // //   }, 'image/png');
    //     // };
    
    //     // img.src = url;
    //   }
}